<!--
/**
 * PameloHR Reports Dashboard
 * Based on OrangeHRM - A comprehensive Human Resource Management (HRM) System
 * Original Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 * Modified work: Copyright (C) 2026 PameloHR
 *
 * PameloHR is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * PameloHR is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with PameloHR.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          Reports Dashboard
        </oxd-text>
        <oxd-text tag="p" class="orangehrm-subtitle">
          Quick access to all your important reports
        </oxd-text>
      </div>

      <div class="reports-grid">
        <!-- Leave Balance Report -->
        <div class="report-card">
          <div class="report-card-icon leave-icon">
            <oxd-icon name="calendar" />
          </div>
          <div class="report-card-content">
            <oxd-text tag="h6" class="report-card-title">
              Leave Balance Report
            </oxd-text>
            <oxd-text tag="p" class="report-card-description">
              View leave balances by employee and leave type (Annual, Sick,
              Paternal, Maternal)
            </oxd-text>
            <div class="report-card-actions">
              <oxd-button
                display-type="secondary"
                label="Generate Report"
                @click="navigateToLeaveReport"
              />
            </div>
          </div>
        </div>

        <!-- Attendance Summary -->
        <div class="report-card">
          <div class="report-card-icon attendance-icon">
            <oxd-icon name="clock" />
          </div>
          <div class="report-card-content">
            <oxd-text tag="h6" class="report-card-title">
              Attendance Summary
            </oxd-text>
            <oxd-text tag="p" class="report-card-description">
              Detailed attendance records with geolocation data and work hours
            </oxd-text>
            <div class="report-card-actions">
              <oxd-button
                display-type="secondary"
                label="View Records"
                @click="navigateToAttendance"
              />
            </div>
          </div>
        </div>

        <!-- Employee Leave Usage -->
        <div class="report-card">
          <div class="report-card-icon usage-icon">
            <oxd-icon name="file-chart" />
          </div>
          <div class="report-card-content">
            <oxd-text tag="h6" class="report-card-title">
              Employee Leave Usage
            </oxd-text>
            <oxd-text tag="p" class="report-card-description">
              Breakdown of leave taken per employee with category details
            </oxd-text>
            <div class="report-card-actions">
              <oxd-button
                display-type="secondary"
                label="Generate Report"
                @click="navigateToLeaveUsage"
              />
            </div>
          </div>
        </div>

        <!-- Custom Leave Report -->
        <div class="report-card">
          <div class="report-card-icon custom-icon">
            <oxd-icon name="sliders" />
          </div>
          <div class="report-card-content">
            <oxd-text tag="h6" class="report-card-title">
              Custom Leave Report
            </oxd-text>
            <oxd-text tag="p" class="report-card-description">
              Build custom reports with flexible date ranges and filters
            </oxd-text>
            <div class="report-card-actions">
              <oxd-button
                display-type="secondary"
                label="Customize"
                @click="openCustomReportModal"
              />
            </div>
          </div>
        </div>

        <!-- PIM Reports -->
        <div class="report-card">
          <div class="report-card-icon pim-icon">
            <oxd-icon name="users" />
          </div>
          <div class="report-card-content">
            <oxd-text tag="h6" class="report-card-title">
              Employee Reports
            </oxd-text>
            <oxd-text tag="p" class="report-card-description">
              Employee demographics, job details, and organizational structure
            </oxd-text>
            <div class="report-card-actions">
              <oxd-button
                display-type="secondary"
                label="View Reports"
                @click="navigateToPIMReports"
              />
            </div>
          </div>
        </div>

        <!-- Time & Project Reports -->
        <div class="report-card">
          <div class="report-card-icon time-icon">
            <oxd-icon name="briefcase" />
          </div>
          <div class="report-card-content">
            <oxd-text tag="h6" class="report-card-title">
              Time & Project Reports
            </oxd-text>
            <oxd-text tag="p" class="report-card-description">
              Project time tracking and timesheet summaries
            </oxd-text>
            <div class="report-card-actions">
              <oxd-button
                display-type="secondary"
                label="View Reports"
                @click="navigateToTimeReports"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Report Modal -->
    <oxd-dialog
      v-if="showCustomReportModal"
      @update:show="closeCustomReportModal"
    >
      <div class="custom-report-modal">
        <h3 class="custom-report-title">Custom Leave Report</h3>
        <oxd-form @submit-valid="generateCustomReport">
          <oxd-form-row>
            <oxd-grid :cols="2" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <employee-autocomplete
                  v-model="customReport.employee"
                  :params="{
                    includeEmployees: 'currentAndPast',
                  }"
                  :label="$t('general.employee_name')"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="customReport.leaveType"
                  type="select"
                  :label="$t('leave.leave_type')"
                  :options="leaveTypes"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-form-row>
            <oxd-grid :cols="2" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <date-input
                  v-model="customReport.fromDate"
                  :label="$t('general.from')"
                  :rules="dateRules"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <date-input
                  v-model="customReport.toDate"
                  :label="$t('general.to')"
                  :rules="dateRules"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />

          <oxd-form-actions>
            <oxd-button
              display-type="ghost"
              label="Cancel"
              @click="closeCustomReportModal"
            />
            <oxd-button
              display-type="secondary"
              label="Generate Report"
              type="submit"
            />
            <oxd-button
              display-type="main"
              label="Export to Excel"
              @click="exportToExcel"
            />
          </oxd-form-actions>
        </oxd-form>
      </div>
    </oxd-dialog>
  </div>
</template>

<script>
import {ref, onMounted} from 'vue';
import {navigate} from '@/core/util/helper/navigation';
import {OxdDialog} from '@ohrm/oxd';
import {required, validDateFormat} from '@ohrm/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import {APIService} from '@/core/util/services/api.service';

export default {
  name: 'ReportsDashboard',
  components: {
    'oxd-dialog': OxdDialog,
    'employee-autocomplete': EmployeeAutocomplete,
  },
  setup() {
    const {userDateFormat} = useDateFormat();
    const showCustomReportModal = ref(false);
    const customReport = ref({
      employee: null,
      leaveType: null,
      fromDate: null,
      toDate: null,
    });

    const leaveTypes = ref([{id: null, label: 'All Leave Types'}]);

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/leave-types',
    );

    // Fetch actual leave types from API
    onMounted(async () => {
      try {
        const response = await http.getAll();
        if (response.data && response.data.data) {
          const types = response.data.data.map((type) => ({
            id: type.id,
            label: type.name,
          }));
          leaveTypes.value = [{id: null, label: 'All Leave Types'}, ...types];
        }
      } catch (error) {
        // Failed to fetch leave types
      }
    });

    const dateRules = [required, validDateFormat(userDateFormat)];

    return {
      showCustomReportModal,
      customReport,
      leaveTypes,
      dateRules,
    };
  },
  methods: {
    navigateToLeaveReport() {
      navigate('/leave/viewLeaveBalanceReport');
    },
    navigateToAttendance() {
      navigate('/attendance/viewAttendanceRecord');
    },
    navigateToLeaveUsage() {
      navigate('/leave/viewLeaveBalanceReport');
    },
    navigateToPIMReports() {
      navigate('/pim/viewDefinedPredefinedReports');
    },
    navigateToTimeReports() {
      navigate('/time/displayProjectReportCriteria');
    },
    openCustomReportModal() {
      this.showCustomReportModal = true;
    },
    closeCustomReportModal() {
      this.showCustomReportModal = false;
    },
    generateCustomReport() {
      const params = new URLSearchParams();
      if (this.customReport.employee) {
        params.append('empNumber', this.customReport.employee.id);
      }
      if (this.customReport.leaveType && this.customReport.leaveType.id) {
        params.append('leaveTypeId', this.customReport.leaveType.id);
      }
      if (this.customReport.fromDate) {
        params.append('fromDate', this.customReport.fromDate);
      }
      if (this.customReport.toDate) {
        params.append('toDate', this.customReport.toDate);
      }
      navigate(`/leave/viewLeaveList?${params.toString()}`);
      this.closeCustomReportModal();
    },
    async exportToExcel() {
      try {
        if (!this.customReport.fromDate || !this.customReport.toDate) {
          this.$toast.error({
            title: 'Validation Error',
            message: 'Please select From Date and To Date',
          });
          return;
        }

        const params = {
          name: 'employee_leave_entitlements_and_usage',
          fromDate: this.customReport.fromDate,
          toDate: this.customReport.toDate,
          limit: 0, // Get all records
        };

        if (this.customReport.employee) {
          params.empNumber = this.customReport.employee.id;
        }

        this.$toast.info({
          title: 'Generating Report',
          message: 'Please wait while we generate your Excel report...',
        });

        const http = new APIService(
          window.appGlobal.baseUrl,
          '/api/v2/leave/reports/data',
        );

        const response = await http.getAll(params);

        if (response.data && response.data.data) {
          // Convert data to CSV
          this.downloadAsExcel(response.data.data, response.data.meta, params);
        }
      } catch (error) {
        this.$toast.error({
          title: 'Export Failed',
          message: 'Failed to generate Excel report. Please try again.',
        });
      }
    },
    downloadAsExcel(data, meta, params) {
      if (!data || data.length === 0) {
        this.$toast.warning({
          title: 'No Data',
          message: 'No leave records found for the selected criteria',
        });
        return;
      }

      // Get employee info from meta
      const employeeName =
        meta && meta.employee
          ? `${meta.employee.firstName || ''} ${
              meta.employee.lastName || ''
            }`.trim()
          : 'All Employees';
      const employeeId =
        meta && meta.employee ? meta.employee.employeeId || '' : '';

      // Create CSV content with proper headers
      const headers = [
        'Employee Name',
        'Employee ID',
        'Leave Type',
        'Entitled (Days)',
        'Pending Approval (Days)',
        'Scheduled (Days)',
        'Taken (Days)',
        'Balance (Days)',
      ];

      const rows = data.map((record) => {
        return [
          employeeName,
          employeeId,
          record.leaveTypeName || '',
          record.entitlementDays || '0.00',
          record.pendingApprovalDays || '0.00',
          record.scheduledDays || '0.00',
          record.takenDays || '0.00',
          record.balanceDays || '0.00',
        ]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const empName = employeeName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `leave_report_${empName}_${params.fromDate}_to_${params.toDate}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.$toast.success({
        title: 'Export Complete',
        message: `Report exported successfully as ${fileName}`,
      });

      this.closeCustomReportModal();
    },
  },
};
</script>

<style scoped lang="scss">
.reports-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
  padding: 24px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.report-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  gap: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
}

.report-card-icon {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;

  &.leave-icon {
    background: #e3f2fd;
    color: #1976d2;
  }

  &.attendance-icon {
    background: #f3e5f5;
    color: #7b1fa2;
  }

  &.usage-icon {
    background: #e8f5e9;
    color: #388e3c;
  }

  &.custom-icon {
    background: #fff3e0;
    color: #f57c00;
  }

  &.pim-icon {
    background: #fce4ec;
    color: #c2185b;
  }

  &.time-icon {
    background: #e0f2f1;
    color: #00796b;
  }
}

.report-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.report-card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.report-card-description {
  font-size: 14px;
  color: #666666;
  line-height: 1.5;
  margin: 0;
  flex: 1;
}

.report-card-actions {
  margin-top: 8px;
}

.custom-report-modal {
  min-width: 600px;
  padding: 20px;

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
}

.custom-report-title {
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 600;
}

.orangehrm-subtitle {
  color: #666666;
  margin-top: 8px;
}
</style>
